import { FC } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationEllipsis,
} from "../ui/pagination";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

interface PaginationGroupProps {
  pagination: {
    totalPages: number;
    hasPreviousPage: boolean;
    pageSize: number;
    page: number;
    hasNextPage: boolean;
  };
  onPageChange?: () => void;
  isLoading?: boolean;
}

export const PaginationGroup: FC<PaginationGroupProps> = ({
  pagination,
  onPageChange,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const handlePageChange = (page: number) => {
    navigate({
      search: (prev) => ({ ...prev, page, pageSize: pagination.pageSize }),
      resetScroll: true,
    });

    // Call the callback to reset container scroll
    onPageChange?.();
  };

  const generatePageNumbers = () => {
    const pages: string[] = [];
    const current = pagination.page;
    const total = pagination.totalPages;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i.toString());
      }
    } else {
      if (current <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i.toString());
        pages.push("ellipsis");
        pages.push(total.toString());
      } else if (current >= total - 3) {
        pages.push("1");
        pages.push("ellipsis");
        for (let i = total - 4; i <= total; i++) pages.push(i.toString());
      } else {
        pages.push("1");
        pages.push("ellipsis");
        for (let i = current - 1; i <= current + 1; i++)
          pages.push(i.toString());
        pages.push("ellipsis");
        pages.push(total.toString());
      }
    }

    return pages;
  };

  if (pagination.totalPages < 1) return null;

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <Button
            variant="ghost"
            size="default"
            className={cn(
              "gap-1 px-2.5 sm:pl-2.5",
              !pagination.hasPreviousPage && "invisible"
            )}
            onClick={() => handlePageChange(pagination.page - 1)}
            aria-label="Go to previous page"
            disabled={isLoading || !pagination.hasPreviousPage}
          >
            <ChevronLeftIcon />
          </Button>
        </PaginationItem>

        {generatePageNumbers().map((page, index) => (
          <PaginationItem key={index}>
            {page === "ellipsis" ? (
              <PaginationEllipsis />
            ) : (
              <Button
                variant={
                  Number.parseInt(page) === pagination.page
                    ? "outline"
                    : "ghost"
                }
                size="icon"
                onClick={() => handlePageChange(Number.parseInt(page))}
                aria-current={
                  Number.parseInt(page) === pagination.page ? "page" : undefined
                }
                disabled={isLoading}
              >
                {page}
              </Button>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <Button
            variant="ghost"
            size="default"
            className={cn(
              "gap-1 px-2.5 sm:pr-2.5",
              !pagination.hasNextPage && "invisible"
            )}
            onClick={() => handlePageChange(pagination.page + 1)}
            aria-label="Go to next page"
            disabled={isLoading || !pagination.hasNextPage}
          >
            <ChevronRightIcon />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};
